import type { Language } from "@/lib/translations";

const MADRID_TZ = "Europe/Madrid";

export type MenuFreshnessSeverity = "fresh" | "recent" | "ok" | "stale";

export type MenuFreshnessLabel =
  | { kind: "today" }
  | { kind: "yesterday" }
  | { kind: "hoursAgo"; hours: number }
  | { kind: "daysAgo"; days: number }
  | { kind: "onDate"; reference: Date };

export type MenuFreshnessResult = {
  label: MenuFreshnessLabel;
  severity: MenuFreshnessSeverity;
};

export type MenuFreshnessInput = {
  /** Primary recency timestamp (captured_at or effective created_at). */
  capturedAt?: string | null;
  /** Optional menu date from OCR (YYYY-MM-DD). */
  menuDate?: string | null;
};

export type MenuFreshnessStrings = {
  uploadedToday: string;
  uploadedYesterday: string;
  uploadedHoursAgo: string;
  uploadedDaysAgo: string;
  uploadedOnDate: string;
};

/** Shared capsule base for freshness/source badges in list and SEO views. */
export const MENU_BADGE_BASE_CLASS =
  "inline-flex min-h-[22px] items-center rounded-full border px-2.5 py-0.5 text-[11px] font-bold leading-none whitespace-nowrap";

function parseTimestamp(iso: string | null | undefined): Date | null {
  if (!iso || typeof iso !== "string" || !iso.trim()) return null;
  const d = new Date(iso);
  return Number.isFinite(d.getTime()) ? d : null;
}

function calendarDayKey(d: Date): string {
  return d.toLocaleDateString("en-CA", { timeZone: MADRID_TZ });
}

function yesterdayKey(now: Date): string {
  const y = new Date(now);
  y.setDate(y.getDate() - 1);
  return calendarDayKey(y);
}

function madridYmd(d: Date): { year: number; month: number; day: number } {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: MADRID_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(d);
  const y = Number(parts.find((p) => p.type === "year")?.value);
  const m = Number(parts.find((p) => p.type === "month")?.value);
  const day = Number(parts.find((p) => p.type === "day")?.value);
  return { year: y, month: m, day };
}

function calendarDaysBetween(earlier: Date, later: Date): number {
  const a = madridYmd(earlier);
  const b = madridYmd(later);
  const earlierUtc = Date.UTC(a.year, a.month - 1, a.day);
  const laterUtc = Date.UTC(b.year, b.month - 1, b.day);
  return Math.floor((laterUtc - earlierUtc) / 86400000);
}

const LOCALE_BY_LANGUAGE: Record<Language, string> = {
  es: "es-ES",
  en: "en-GB",
  ca: "ca-ES",
};

function formatUploadDate(reference: Date, language: Language): string {
  const { year } = madridYmd(reference);
  const currentYear = madridYmd(new Date()).year;
  return new Intl.DateTimeFormat(LOCALE_BY_LANGUAGE[language], {
    timeZone: MADRID_TZ,
    day: "numeric",
    month: "long",
    ...(year !== currentYear ? { year: "numeric" } : {}),
  }).format(reference);
}

function applyTemplate(template: string, values: Record<string, string | number>): string {
  return Object.entries(values).reduce(
    (text, [key, value]) => text.replaceAll(`{${key}}`, String(value)),
    template
  );
}

export function formatMenuFreshnessLabel(
  label: MenuFreshnessLabel,
  strings: MenuFreshnessStrings,
  language: Language
): string {
  switch (label.kind) {
    case "today":
      return strings.uploadedToday;
    case "yesterday":
      return strings.uploadedYesterday;
    case "hoursAgo":
      return applyTemplate(strings.uploadedHoursAgo, { hours: label.hours });
    case "daysAgo":
      return applyTemplate(strings.uploadedDaysAgo, { count: label.days });
    case "onDate":
      return applyTemplate(strings.uploadedOnDate, {
        date: formatUploadDate(label.reference, language),
      });
  }
}

/**
 * Upload-date freshness for public menus (label resolved via translations).
 */
export function getMenuFreshness(input: MenuFreshnessInput): MenuFreshnessResult | null {
  const menuDateOnly =
    typeof input.menuDate === "string" && /^\d{4}-\d{2}-\d{2}/.test(input.menuDate)
      ? input.menuDate.slice(0, 10)
      : null;

  const captured = parseTimestamp(input.capturedAt);
  if (!captured && !menuDateOnly) return null;

  const now = new Date();
  const reference = captured ?? new Date(`${menuDateOnly}T12:00:00`);

  const todayKey = calendarDayKey(now);
  const refDayKey = menuDateOnly ?? calendarDayKey(reference);
  const yKey = yesterdayKey(now);

  if (refDayKey === todayKey) {
    return { label: { kind: "today" }, severity: "fresh" };
  }
  if (refDayKey === yKey) {
    return { label: { kind: "yesterday" }, severity: "ok" };
  }

  const msAgo = now.getTime() - reference.getTime();
  const hoursAgo = Math.floor(msAgo / 3_600_000);
  if (hoursAgo >= 1 && hoursAgo < 24) {
    return { label: { kind: "hoursAgo", hours: hoursAgo }, severity: "recent" };
  }

  const dayAnchor = menuDateOnly ? new Date(`${menuDateOnly}T12:00:00`) : reference;
  const days = calendarDaysBetween(dayAnchor, now);
  if (days >= 2 && days < 28) {
    return {
      label: { kind: "daysAgo", days },
      severity: days <= 7 ? "ok" : "stale",
    };
  }

  return { label: { kind: "onDate", reference }, severity: "stale" };
}

export function freshnessBadgeClassName(severity: MenuFreshnessSeverity): string {
  switch (severity) {
    case "fresh":
      return "bg-emerald-600 text-white border-emerald-500";
    case "recent":
      return "bg-emerald-600 text-white border-emerald-500";
    case "ok":
      return "bg-amber-600 text-white border-amber-500";
    case "stale":
      return "bg-slate-100 text-slate-700 border-slate-300 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-600";
  }
}
